// src/components/blocks/Stair.jsx
import React, { useContext, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { DeckContext } from '../../context/DeckContextV3';
import BaseBlock from './BaseBlockV3';
import EdgeIndicator from './edgeIndicatorV3/EdgeIndicatorV3';
import { formatFeetInches } from '../../utils/measurementUtils';
import { StairBlockProps } from '../../../../../../types/shared/propsV3';
import EDGE_WIDTH from '../../../../../../constants/edgeWidth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EdgeLabel from 'src/enums/edgeLabel';
import { getOppositeEdge } from '../../utils/edgeUtils';
import EdgeType from 'src/enums/edgeType';

const Stair: React.FC<StairBlockProps> = ({
	label,
	stair,
	onSelect,
	onUpdateBlock,
	setIsDragging,
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const nodeRef = useRef(null); // Create a ref for the DOM node

	const { scaleFactor } = state;

	const stairWidth = stair.displayDimensions.width;
	const stairHeight = stair.displayDimensions.height;

	// Determine the attachment orientation
	const blockAttachedEdge = stair.edges.find((edge) => edge.attachedToParent);

	useEffect(() => {
		const { ref, ...stairWithoutRef } = stair;
		dispatch({
			type: 'UPDATE_BLOCK',
			payload: {
				...stairWithoutRef,
				edges: stair.edges.map((edge) => {
					const updatedEdge = { ...edge };

					if ((stair.dimensions.endHeight || 0) > 0) {
						updatedEdge.segments = updatedEdge.segments.map((segment) => ({
							...segment,
							type:
								segment.type == EdgeType.GROUND
									? EdgeType.RAILING
									: segment.type,
						}));
					} else if (edge.label == getOppositeEdge(blockAttachedEdge?.label)) {
						updatedEdge.segments = updatedEdge.segments.map((segment) => ({
							...segment,
							type:
								segment.type == EdgeType.RAILING
									? EdgeType.GROUND
									: segment.type,
						}));
					}

					return updatedEdge;
				}),
			},
		});
	}, [stair.dimensions.endHeight]);

	const isHorizontal =
		blockAttachedEdge?.label == EdgeLabel.LEFT ||
		blockAttachedEdge?.label == EdgeLabel.RIGHT;

	let direction = blockAttachedEdge?.label;

	if (stair.dimensions.startHeight > stair.dimensions.endHeight) {
		direction = getOppositeEdge(blockAttachedEdge?.label);
	}

	return (
		<BaseBlock
			block={stair}
			onSelect={onSelect}
			onUpdateBlock={onUpdateBlock}
			setIsDragging={setIsDragging}
			disabled={false} // Always draggable
			nodeRef={nodeRef} // Pass nodeRef to BaseBlock
		>
			<Box
				ref={nodeRef} // Attach nodeRef to the DOM node
				style={{ transform: `rotate(${stair.rotation || 0}deg)` }}
				sx={{
					width: stairWidth * scaleFactor, // e.g., 40px
					height: stairHeight * scaleFactor, // e.g., 40px
					bgcolor: '#FF9800', // Orange background
					cursor: 'pointer',
					position: 'absolute',
					border: '0.5px solid rgba(255, 255, 255, 0.1)',
				}}
				data-test={`stair-${label.toLowerCase().replace(/\s+/g, '-')}`}
				onClick={(e) => {
					e.stopPropagation();
					onSelect(stair);
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
				}}
				onTouchEnd={(e) => {
					e.stopPropagation();
					e.preventDefault();
					onSelect(stair);
				}}
			>
				<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
					{/* Edge Indicators */}
					{stair.edges.map((edge) => (
						<EdgeIndicator
							key={edge.label}
							edge={edge}
							onSelectEdge={(label) => {
								dispatch({
									type: 'SELECT_BLOCK',
									payload: stair,
								});

								dispatch({
									type: 'SELECT_EDGE',
									payload: { blockId: stair.id, edgeLabel: label },
								});
							}}
							isSelected={
								state.selectedEdge?.blockId == stair.id &&
								state.selectedEdge?.edgeLabel === edge.label
							}
							parentIsSelected={state.selectedBlock?.id === stair.id}
						/>
					))}
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							paddingTop: `${EDGE_WIDTH + 5}px`,
							cursor: 'pointer',
						}}
						onClick={(e) => {
							e.stopPropagation();
							onSelect(stair);
						}}
						onTouchStart={(e) => {
							e.stopPropagation();
						}}
						onTouchEnd={(e) => {
							e.stopPropagation();
							e.preventDefault();
							onSelect(stair);
						}}
					>
						<Box
							sx={{
								width: '100%',
								display: 'flex',
								justifyContent: 'center',
								paddingTop: `${EDGE_WIDTH + 5}px`,
								cursor: 'pointer',
							}}
							onClick={(e) => {
								e.stopPropagation();
								onSelect(stair);
							}}
							onTouchStart={(e) => {
								e.stopPropagation();
							}}
							onTouchEnd={(e) => {
								e.stopPropagation();
								e.preventDefault();
								onSelect(stair);
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: isHorizontal ? 'row' : 'column',
									gap: '5px',
									...(isHorizontal && { marginTop: '-10px' }),
								}}
							>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										...(isHorizontal &&
											(blockAttachedEdge.label == EdgeLabel.LEFT
												? { marginLeft: '-10px' }
												: { marginRight: '-10px' })),
									}}
								>
									{direction == EdgeLabel.LEFT && <ArrowBackIcon />}
									{direction == EdgeLabel.TOP && <ArrowUpwardIcon />}
								</Box>

								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										...(isHorizontal &&
											(blockAttachedEdge?.label == EdgeLabel.RIGHT
												? { flexDirection: 'row-reverse' }
												: { flexDirection: 'row' })),
									}}
								>
									<Typography
										variant="caption"
										lineHeight={1.15}
										color="rgba(0, 0, 0, 0.6)"
										noWrap
										maxWidth={stairWidth}
										zIndex={10}
										sx={{
											...(isHorizontal &&
												(blockAttachedEdge.label == EdgeLabel.RIGHT
													? { transform: 'rotate(90deg)' }
													: { transform: 'rotate(270deg)' })),
										}}
									>
										<p>
											<b>{label}</b>
										</p>
										{formatFeetInches(stair.dimensions.depth)}
										<span className="px-0.5">&times;</span>
										{formatFeetInches(stair.dimensions.width)}
									</Typography>

									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											...(isHorizontal &&
												(blockAttachedEdge.label == EdgeLabel.LEFT
													? { marginLeft: '-10px' }
													: { marginRight: '-10px' })),
											...(isHorizontal &&
												(blockAttachedEdge.label == EdgeLabel.RIGHT
													? { transform: 'rotate(90deg)' }
													: { transform: 'rotate(270deg)' })),
										}}
									>
										<svg
											width="24"
											height="24"
											viewBox="0 0 48 48"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<rect width="48" height="48" fill="#FF9800" />
											<path
												d="M28.833 7.5V18.0078H18.167V28.5146H7.5V40.5H40.5V7.5H28.833ZM39.5 39.5H8.5V29.5146H19.167V19.0078H29.833V8.5H39.5V39.5Z"
												fill="#663D00"
												stroke="black"
											/>
										</svg>

										{stair.dimensions.numberOfSteps}
									</Box>
								</Box>

								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										...(isHorizontal &&
											(blockAttachedEdge.label == EdgeLabel.LEFT
												? { marginLeft: '-10px' }
												: { marginRight: '-10px' })),
									}}
								>
									{direction == EdgeLabel.RIGHT && <ArrowForwardIcon />}
									{direction == EdgeLabel.BOTTOM && <ArrowDownwardIcon />}
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</BaseBlock>
	);
};

export default Stair;
